import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Settings, Loader2, Database, Server, HardDrive, Users, AlertCircle, CheckCircle2, Info, ShieldCheck } from "lucide-react";
import { useSystemStats, useAdminUsers, usePendingActions } from "../../hooks/useAdminHub";

const ACTION_ICON = {
  warning: { icon: AlertCircle, cls: "text-amber-500", bg: "bg-amber-50 border-amber-200" },
  info: { icon: Info, cls: "text-blue-500", bg: "bg-blue-50 border-blue-200" },
  success: { icon: CheckCircle2, cls: "text-emerald-500", bg: "bg-emerald-50 border-emerald-200" },
};

export const AdminHubTab = () => {
  const { data: systemStats, isLoading: statsLoading } = useSystemStats();
  const { data: usersData, isLoading: usersLoading, error: usersError } = useAdminUsers();
  const { data: pendingData, isLoading: pendingLoading } = usePendingActions();

  return (
    <div className="space-y-6" data-testid="adminhub-tab">
      {/* Pending Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            Pending Actions
          </CardTitle>
          <CardDescription>Items requiring admin attention</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />Loading pending actions...
            </div>
          )}
          {pendingData && (
            <>
              {/* Summary row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: "Users Missing BN", value: pendingData.summary?.users_missing_bn, color: "amber" },
                  { label: "Unembedded Projects", value: pendingData.summary?.projects_without_embeddings, color: "blue" },
                  { label: "Total Users", value: pendingData.summary?.total_users, color: "teal" },
                  { label: "Recent Signups (7d)", value: pendingData.recent_signups?.length || 0, color: "violet" },
                ].map((s) => (
                  <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-lg p-3 text-center`}>
                    <p className={`text-2xl font-bold text-${s.color}-700`}>{s.value ?? "—"}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Action items */}
              <div className="space-y-2">
                {(pendingData.actions || []).map((action, i) => {
                  const cfg = ACTION_ICON[action.type] || ACTION_ICON.info;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${cfg.bg}`}>
                      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cfg.cls}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900">{action.title}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{action.detail}</p>
                      </div>
                      {action.action && (
                        <Badge variant="outline" className="text-xs flex-shrink-0">{action.action}</Badge>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Recent signups */}
              {pendingData.recent_signups?.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-xs font-medium text-zinc-500 uppercase mb-2">Recent Signups (7 days)</p>
                  <div className="space-y-2">
                    {pendingData.recent_signups.map((u) => (
                      <div key={u.id} className="flex items-center justify-between text-sm bg-zinc-50 rounded-lg px-3 py-2">
                        <span className="text-zinc-700">{u.email}</span>
                        <div className="flex items-center gap-2">
                          {u.charity_bn && <Badge variant="outline" className="text-xs">{u.charity_bn}</Badge>}
                          <span className="text-xs text-zinc-400">{new Date(u.created_at).toLocaleDateString("en-CA")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Management */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            User Management
          </CardTitle>
          <CardDescription>All registered users and their project activity</CardDescription>
        </CardHeader>
        <CardContent>
          {usersLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />Loading users...
            </div>
          )}
          {usersError && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertTitle className="text-red-900">Access Denied</AlertTitle>
              <AlertDescription className="text-red-800">
                {usersError.response?.status === 403
                  ? "Admin access required to view user management."
                  : usersError.message}
              </AlertDescription>
            </Alert>
          )}
          {usersData && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Email</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">BN</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Projects</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Role</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {(usersData.users || usersData).map((u) => (
                    <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="py-2.5 px-3 text-zinc-700">{u.email}</td>
                      <td className="py-2.5 px-3">
                        {u.charity_bn
                          ? <span className="font-mono text-xs bg-zinc-100 px-2 py-0.5 rounded">{u.charity_bn}</span>
                          : <span className="text-zinc-300 text-xs">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`font-semibold ${u.project_count > 0 ? "text-cyan-700" : "text-zinc-300"}`}>
                          {u.project_count ?? 0}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {u.is_admin
                          ? <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>
                          : <Badge variant="outline" className="text-xs">User</Badge>}
                      </td>
                      <td className="py-2.5 px-3 text-zinc-400 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("en-CA") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Statistics */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Platform Statistics</CardTitle>
          <CardDescription>Real-time system and database information</CardDescription>
        </CardHeader>
        <CardContent>
          {statsLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />Loading system statistics...
            </div>
          )}
          {systemStats && (
            <div className="space-y-6">
              {systemStats.platform && (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 mb-1">Name</p>
                    <p className="text-lg font-semibold text-zinc-900">{systemStats.platform.name}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 mb-1">Version</p>
                    <p className="text-lg font-semibold text-zinc-900">{systemStats.platform.version}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 mb-1">Status</p>
                    <Badge className="bg-emerald-100 text-emerald-700">{systemStats.platform.status}</Badge>
                  </div>
                </div>
              )}
              {systemStats.data && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><Database className="h-4 w-4 text-teal-600" /><p className="text-xs text-zinc-500 uppercase font-medium">Canadian Charities</p></div>
                    <p className="text-2xl font-bold text-teal-600">{systemStats.data.canadian_charities}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><Database className="h-4 w-4 text-cyan-600" /><p className="text-xs text-zinc-500 uppercase font-medium">US Nonprofits</p></div>
                    <p className="text-2xl font-bold text-cyan-600">{systemStats.data.us_nonprofits}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><HardDrive className="h-4 w-4 text-violet-600" /><p className="text-xs text-zinc-500 uppercase font-medium">Total Records</p></div>
                    <p className="text-2xl font-bold text-violet-600">{systemStats.data.total_records}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><Server className="h-4 w-4 text-amber-600" /><p className="text-xs text-zinc-500 uppercase font-medium">Data Years</p></div>
                    <p className="text-2xl font-bold text-amber-600">{systemStats.data.data_years}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHubTab;
